import { Field, FieldDescription, FieldLabel } from "@screener/uikit/components/field";
import { PanelRow, PanelRowText } from "@screener/uikit/components/panel";
import { cn } from "@screener/uikit/lib/utils";
import { type ReactNode, useId } from "react";

const LABEL_CLASS = "text-[13px] leading-[1.3] font-medium";
const HINT_CLASS = "text-xs leading-[1.45] text-pretty text-muted-foreground";

/**
 * Строка настройки с одним полем ввода: подпись и подсказка слева, поле
 * справа. Подпись и подсказка связаны с полем через `Field` (htmlFor и
 * aria-describedby ставит примитив), поле — прямой потомок `children`.
 */
export function SettingsRow({
  label,
  hint,
  htmlFor,
  invalid,
  children,
}: {
  label: string;
  hint?: ReactNode;
  /** `id` поля: подпись ссылается на него явно (нужно и статической разметке). */
  htmlFor?: string;
  invalid?: boolean;
  children: ReactNode;
}) {
  return (
    <PanelRow render={<Field invalid={invalid} />}>
      <PanelRowText>
        <FieldLabel htmlFor={htmlFor} className={LABEL_CLASS}>
          {label}
        </FieldLabel>
        {hint ? <FieldDescription className={HINT_CLASS}>{hint}</FieldDescription> : null}
      </PanelRowText>
      <div className="flex min-w-0 flex-col gap-2">{children}</div>
    </PanelRow>
  );
}

/**
 * Строка с несколькими контролами или без поля ввода (адрес и порт,
 * плашка «задан»): группа с названием и описанием, контролы подписывает сам
 * экран.
 */
export function SettingsGroupRow({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const labelId = useId();
  const hintId = useId();
  return (
    <PanelRow role="group" aria-labelledby={labelId} aria-describedby={hint ? hintId : undefined}>
      <PanelRowText>
        <div id={labelId} className={LABEL_CLASS}>
          {label}
        </div>
        {hint ? (
          <p id={hintId} className={HINT_CLASS}>
            {hint}
          </p>
        ) : null}
      </PanelRowText>
      <div className={cn("flex min-w-0 flex-col gap-2", className)}>{children}</div>
    </PanelRow>
  );
}
